/**
 * PatientEmptyState Component
 *
 * Empty state for when no patients are found
 */

import React from 'react';
import { useSubjectLanguage } from '@features/user';
import { PersonAdd as PersonAddIcon, SearchOff as SearchOffIcon } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import { capitalize } from '../../../utils/utils';

export interface PatientEmptyStateProps {
  isSearchActive: boolean;
  searchTerm: string;
  onNewPatient: () => void;
  onClearSearch: () => void;
  canCreatePatients?: boolean;
}

export const PatientEmptyState: React.FC<PatientEmptyStateProps> = ({
  isSearchActive,
  searchTerm,
  onNewPatient,
  onClearSearch,
  canCreatePatients = true,
}) => {
  const theme = useTheme();
  const subjectLanguage = useSubjectLanguage();

  if (isSearchActive) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          py: 8,
          px: 3,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            bgcolor: alpha(theme.palette.grey[500], 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <SearchOffIcon sx={{ fontSize: 28, color: theme.palette.grey[400] }} />
        </Box>

        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}
        >
          No results found
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: theme.palette.text.secondary, maxWidth: 320, mb: 3 }}
        >
          No {subjectLanguage}s matching &ldquo;{searchTerm}&rdquo;
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClearSearch}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            Clear Search
          </Button>
          {canCreatePatients && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddIcon />}
              onClick={onNewPatient}
              sx={{ textTransform: 'none', fontWeight: 500 }}
            >
              New {capitalize(subjectLanguage)}
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // No patients yet
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 8,
        px: 3,
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
        }}
      >
        <PersonAddIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
      </Box>

      <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 0.5 }}>
        Add your first {subjectLanguage}
      </Typography>

      <Typography
        variant="body2"
        sx={{ color: theme.palette.text.secondary, maxWidth: 360, mb: 3 }}
      >
        Get started by adding a {subjectLanguage}. JotPsych will use information from previous
        encounters to create more accurate notes.
      </Typography>

      {canCreatePatients && (
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={onNewPatient}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Add {capitalize(subjectLanguage)}
        </Button>
      )}
    </Box>
  );
};
